﻿import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { SVGIcon } from '@progress/kendo-svg-icons';
import clsx from 'clsx';
import { NavLink } from 'react-router';
import {
    AirportListOutline,
    AirportListSolid,
    AirportTowerOutline,
    AirportTowerSolid,
    ChartsComboOutline,
    ChartsComboSolid,
    CogOutline,
    CogSolid,
    DoubleChevronLeftSolid,
    FlagOutline,
    FlagSolid,
    FlightListOutline,
    FlightListSolid,
    HomeOutline,
    HomeSolid,
    ReportOutline,
    ReportSolid,
    UserAvatarOutline,
    UserAvatarSolid
} from '../../icons/icons';
import { SystemHubImportanceLevel } from '../../types/systemhub-notification';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { setSetting } from '../state/slices/settings-slice';
import './NavMenu.css';
import { ROUTES } from './routes';

// prettier-ignore
const upperItems = [
    { text: 'Home', icon: HomeOutline, route: ROUTES.HOME, activeIcon: HomeSolid, id: 'nav-home' },
    { text: 'Flight list', icon: FlightListOutline, route: ROUTES.FLIGHT_LIST, activeIcon: FlightListSolid, id: 'nav-flight-list' },
    { text: 'Airport list', icon: AirportListOutline, route: ROUTES.AIRPORT_LIST, activeIcon: AirportListSolid, id: 'nav-airport-list' },
    { text: 'Airport summary', icon: AirportTowerOutline, route: ROUTES.AIRPORT_SUMMARY, activeIcon: AirportTowerSolid, id:'nav-airport-summary' },
    { text: 'Reports', icon: ReportOutline, route: ROUTES.REPORTS, activeIcon: ReportSolid, id: 'nav-report' },
    { text: "Dashboard", icon: ChartsComboOutline, route: ROUTES.DASHBOARD, activeIcon: ChartsComboSolid, id: 'nav-dashboard' }
];

export default function NavMenu() {
    const showCustomerAdmin = useAppSelector((x) => x.user.rights.customerAdmin);
    const showDashboard = useAppSelector((x) => x.user.rights.dashboard);
    const expanded = useAppSelector((x) => x.settings.leftNavOpen);
    const systemHubImportance = useAppSelector((x) => x.global.systemHubNotification.importance);
    const systemHubColor = importanceToColor(systemHubImportance);
    const dispatch = useAppDispatch();

    const toggleExpanded = () => {
        dispatch(setSetting('leftNavOpen', !expanded));
    };

    return (
        <div
            id="main-left-nav"
            className={clsx('d-flex flex-column primary-bg-color h-100 nav-container')}
            style={{ width: expanded ? 200 : 48 }}>
            <div className={'flex-grow-1'}>
                {upperItems
                    .filter((item) => item.route !== ROUTES.DASHBOARD || showDashboard)
                    .map((item) => (
                        <NavItem {...item} key={item.route} expanded={expanded} />
                    ))}
            </div>
            <div className="border-top border-bottom">
                <NavItem
                    id="nav-system-hub"
                    text="System hub"
                    icon={FlagOutline}
                    activeIcon={FlagSolid}
                    route={ROUTES.SYSTEM_HUB}
                    expanded={expanded}
                    iconColor={systemHubColor}
                />

                {showCustomerAdmin && (
                    <NavItem
                        id="nav-customer-admin"
                        text="Customer Admin"
                        icon={UserAvatarOutline}
                        activeIcon={UserAvatarSolid}
                        route={ROUTES.CUSTOMER_ADMIN}
                        expanded={expanded}
                    />
                )}

                <NavItem
                    id="nav-settings"
                    text="Settings"
                    icon={CogOutline}
                    activeIcon={CogSolid}
                    route={ROUTES.SETTINGS}
                    expanded={expanded}
                />
            </div>

            <div className={clsx('d-flex justify-content-end align-items-center pe-2')} style={{ height: 48 }}>
                <Button
                    className={clsx('nav-chevron', expanded && 'expanded')}
                    svgIcon={DoubleChevronLeftSolid}
                    fillMode={'flat'}
                    onClick={toggleExpanded}
                />
            </div>
        </div>
    );
}

function NavItem(props: {
    id?: string;
    route: string;
    activeIcon: SVGIcon;
    icon: SVGIcon;
    text: string;
    expanded: boolean;
    iconColor?: string;
}) {
    return (
        <NavLink to={props.route} className="nav-link" id={props.id}>
            {(navLinkProps) => {
                return (
                    <div className={clsx('nav-item-content d-flex', navLinkProps.isActive && 'active')}>
                        <div className={clsx('d-flex align-items-center', navLinkProps.isActive && 'active')}>
                            <div className="nav-icon-container">
                                <SvgIcon
                                    size="large"
                                    icon={navLinkProps.isActive ? props.activeIcon : props.icon}
                                    color={props.iconColor}
                                />
                            </div>
                            <span className={clsx('ms-0 nav-text', !props.expanded && 'collapsed')}>{props.text}</span>
                        </div>
                    </div>
                );
            }}
        </NavLink>
    );
}

function importanceToColor(importance: SystemHubImportanceLevel): string {
    switch (importance) {
        case SystemHubImportanceLevel.none:
            return '';
        case SystemHubImportanceLevel.green:
            return 'var(--color-semantic-green)';
        case SystemHubImportanceLevel.yellow:
            return 'var(--color-semantic-yellow)';
        case SystemHubImportanceLevel.red:
            return 'var(--color-semantic-red)';
    }
}
